import { type ParsedUrlQuery } from 'querystring';

import { FetchItinerariesFilter } from '../../../../resources/itinerary/api/pwapi/itinerary';
import { ItinerariesListAvailableFilters } from '../../../../resources/itinerary/domain/types/Itinerary';

export type ItinerariesListFilter =
    | Extract<FetchItinerariesFilter, { key: 'duration' }>
    | Extract<FetchItinerariesFilter, { key: 'price' }>
    | Extract<FetchItinerariesFilter, { key: 'activity-type' }>
    | Extract<FetchItinerariesFilter, { key: 'type' }>
    | Extract<FetchItinerariesFilter, { key: 'environment-type' }>
    | Extract<FetchItinerariesFilter, { key: 'type-of-profile' }>
    | Extract<FetchItinerariesFilter, { key: 'guidance' }>
    | Extract<FetchItinerariesFilter, { key: 'range' }>;

export function getFiltersQueryParams(
    queryParams: ParsedUrlQuery,
    availableFilters: ItinerariesListAvailableFilters['filters'],
): ParsedUrlQuery {
    const filtersQueryParams: ParsedUrlQuery = {};

    availableFilters.forEach((availableFilter) => {
        const filterName = availableFilter.name;

        // Special case for price.
        if (filterName === 'price') {
            if (queryParams['priceMin']) {
                filtersQueryParams['priceMin'] = queryParams['priceMin'];
            }
            if (queryParams['priceMax']) {
                filtersQueryParams['priceMax'] = queryParams['priceMax'];
            }
            return;
        }

        if (queryParams[filterName]) {
            filtersQueryParams[filterName] = queryParams[filterName];
        }
    });

    return filtersQueryParams;
}

export function queryParamsAsApiFilters(
    queryParams: ParsedUrlQuery,
    availableFilters: ItinerariesListAvailableFilters['filters'],
): Array<ItinerariesListFilter> {
    return availableFilters.reduce<Array<ItinerariesListFilter>>((filters, filter) => {
        const filterName = filter.name;

        // This special case filter is annoying.
        if (filter.type === 'min-max' && filter.name === 'price') {
            const priceFilter = {
                key: 'price',
                value: {
                    min: queryParams['priceMin'],
                    max: queryParams['priceMax'],
                },
            };

            if (isEmptyFilterValue(priceFilter.value.min)) {
                delete priceFilter.value.min;
            }
            if (isEmptyFilterValue(priceFilter.value.max)) {
                delete priceFilter.value.max;
            }
            if (
                isEmptyFilterValue(priceFilter.value.min) &&
                isEmptyFilterValue(priceFilter.value.max)
            ) {
                return filters;
            }

            return filters.concat([
                priceFilter as Extract<FetchItinerariesFilter, { key: 'price' }>,
            ]);
        }

        const filterValue = queryParams[filterName];

        if (isEmptyFilterValue(filterValue)) {
            return filters;
        }

        if (Array.isArray(filterValue)) {
            return filters.concat([
                {
                    key: filterName,
                    value: filterValue,
                } as ItinerariesListFilter,
            ]);
        }

        return filters.concat([
            {
                key: filterName,
                value: filterValue,
            } as ItinerariesListFilter,
        ]);
    }, []);
}

function isEmptyFilterValue(value: string | Array<string> | undefined): value is undefined | '' {
    return value === undefined || value === '';
}
